import { JSX } from "react";
import { useGetProductDownloadsQuery } from "../../common/api/productDetails";
import { ProductDetailsBody } from "../../common/components/productDetail/ProductDetailsBody";
import { ProductDetailsCarousel } from "../../common/components/productDetail/ProductDetailsCarousel";
import { useProductAssociatedProducts } from "../../common/hooks/productDetail/useProductAssociatedProducts";
import { showProductCarouselSection } from "../../common/utils/productDetail/productDetailSectionVisibility";
import { useProductDetailGallery } from "../../common/hooks/productDetail/useProductDetailGallery";
import { useRefurbishedProductDetail } from "./hooks/useRefurbishedProductDetail";
import { RefurbishedProductDetailsHeader } from "./components/RefurbishedProductDetailsHeader";
import { RefurbishedProductDetailsInfoBar } from "./components/RefurbishedProductDetailsInfoBar";
import { CartToastContainer } from "../../common/components/cartToast/CartToastContainer";

const RefurbishedProductDetailsPageContainer = (): JSX.Element | null => {
    const {
        data,
        isError,
        isPageLoading,
        translationsLoaded,
        activeLanguage,
        userData,
        productName,
        pricing,
        categoryBreadcrumbs,
        reserveButtonVariant,
        reserveButtonLabel,
        canReserve,
        isDownloading,
        isReservingProduct,
        labels,
        reserveProductById,
        handleDownload,
    } = useRefurbishedProductDetail();

    const productId = data?.id != null ? String(data.id) : "";
    const activeCountryId = userData?.data?.activeCountry?.id;
    const shouldFetchDownloads =
        !!productId && !!activeLanguage && !!activeCountryId;

    const { data: downloads, isLoading: isDownloadsLoading } =
        useGetProductDownloadsQuery(
            { productId, countryId: activeCountryId ?? 0 },
            { skip: !shouldFetchDownloads },
        );

    const {
        accessories,
        similarProducts,
        addOns,
        olderModels,
        isAssociationsLoading,
    } = useProductAssociatedProducts(data?.id, activeCountryId, isPageLoading);

    const openGallery = useProductDetailGallery();
    const communicationEmail = userData?.data?.communicationEmail ?? null;

    if (isError) {
        return null;
    }

    return (
        <div className="p-product-details p-refurbished-product-details">
            <RefurbishedProductDetailsHeader
                productName={productName}
                hasData={!!data}
                isPageLoading={isPageLoading}
                activeLanguage={activeLanguage}
            />

            {showProductCarouselSection(
                isPageLoading,
                data?.productImages?.length ?? 0,
            ) && (
                <ProductDetailsCarousel
                    isLoading={isPageLoading}
                    images={data?.productImages ?? []}
                    onImageClick={openGallery}
                />
            )}

            <RefurbishedProductDetailsInfoBar
                data={data}
                isPageLoading={isPageLoading}
                activeLanguage={activeLanguage}
                productName={productName}
                pricing={pricing}
                categoryBreadcrumbs={categoryBreadcrumbs}
                labels={labels}
                reserveButtonLabel={reserveButtonLabel}
                reserveButtonVariant={reserveButtonVariant}
                canReserve={canReserve}
                isDownloading={isDownloading}
                isReservingProduct={isReservingProduct}
                communicationEmail={communicationEmail}
                onDownload={handleDownload}
                onReserve={reserveProductById}
            />

            <ProductDetailsBody
                data={data}
                isPageLoading={isPageLoading}
                translationsLoaded={translationsLoaded}
                activeLanguage={activeLanguage}
                accessories={accessories}
                similarProducts={similarProducts}
                addOns={addOns}
                olderModels={olderModels}
                isAssociationsLoading={isAssociationsLoading}
                marketingDownloads={downloads?.marketing ?? {}}
                technicalDownloads={downloads?.technical ?? {}}
                marketingDownloadsCount={downloads?.marketingCount ?? 0}
                technicalDownloadsCount={downloads?.technicalCount ?? 0}
                downloadsLoading={isDownloadsLoading}
            />

            <CartToastContainer />
        </div>
    );
};

export default RefurbishedProductDetailsPageContainer;
